import {
	createContext,
	ReactNode,
	useContext,
	useEffect,
	useMemo,
} from "react";
import {
	ClientConnector,
	ClientToHostMessage,
	HostToClientMessage,
	MessagePayload,
} from "@rubyplay/effw-connector";
import type { UiSnapshotPayload } from "@/integrations/snapshots/UiSnapshotTypes";
import { debugWarn } from "@/utils";

export type Unsubscribe = () => void;

export interface CommunicatorContextValue {
	connector: ClientConnector;

	isStandalone(): boolean;

	onHostError(cb: (p: MessagePayload<HostToClientMessage, "Error">) => void): Unsubscribe;
	onConfigConnection(cb: (p: MessagePayload<HostToClientMessage, "ConfigConnection">) => void): Unsubscribe;
	onStartupPreload(cb: (p: MessagePayload<HostToClientMessage, "StartupPreload">) => void): Unsubscribe;
	onGameReady(cb: (p: MessagePayload<HostToClientMessage, "GameReady">) => void): Unsubscribe;

	onBetUpdated(cb: (p: MessagePayload<HostToClientMessage, "BetUpdated">) => void): Unsubscribe;
	onPromotionStarted(cb: (p: MessagePayload<HostToClientMessage, "PromotionStarted">) => void): Unsubscribe;
	onPromotionEnded(cb: (p: MessagePayload<HostToClientMessage, "PromotionEnded">) => void): Unsubscribe;
	onBuyFeatureStarted(cb: (p: MessagePayload<HostToClientMessage, "BuyFeatureStarted">) => void): Unsubscribe;
	onBuyFeatureEnded(cb: (p: MessagePayload<HostToClientMessage, "BuyFeatureEnded">) => void): Unsubscribe;
	onPaidRoundStarted(cb: (p: MessagePayload<HostToClientMessage, "PaidRoundStarted">) => void): Unsubscribe;
	onPaidRoundEnded(cb: (p: MessagePayload<HostToClientMessage, "PaidRoundEnded">) => void): Unsubscribe;
	onRoundStarted(cb: (p: MessagePayload<HostToClientMessage, "RoundStarted">) => void): Unsubscribe;
	onRoundEnded(cb: (p: MessagePayload<HostToClientMessage, "RoundEnded">) => void): Unsubscribe;
	onAnimationsStarted(cb: (p: MessagePayload<HostToClientMessage, "AnimationsStarted">) => void): Unsubscribe;
	onAnimationsEnded(cb: (p: MessagePayload<HostToClientMessage, "AnimationsEnded">) => void): Unsubscribe;

	onSnapshotMode(cb: (p: MessagePayload<HostToClientMessage, "SnapshotMode">) => void): Unsubscribe;
	onSnapshotRequest(cb: (p: MessagePayload<HostToClientMessage, "SnapshotRequest">) => void): Unsubscribe;

	ackInitConnection(p: MessagePayload<ClientToHostMessage, "InitConnectionAck">): void;
	ackConfigConnection(p: MessagePayload<ClientToHostMessage, "ConfigConnectionAck">): void;

	demandFullscreen(): void;
	exitFullscreen(): void;
	onFullscreenGranted(cb: (p: MessagePayload<HostToClientMessage, "FullscreenGranted">) => void): Unsubscribe;
	onExitFullscreenAck(cb: (p: MessagePayload<HostToClientMessage, "ExitFullscreenAck">) => void): Unsubscribe;
	onOrientationChange(cb: (p: MessagePayload<HostToClientMessage, "Orientation">) => void): Unsubscribe;

	onHideAllFreeAreas(cb: (p: MessagePayload<HostToClientMessage, "HideAllFreeAreas">) => void): Unsubscribe;
	onShowAllFreeAreas(cb: (p: MessagePayload<HostToClientMessage, "ShowAllFreeAreas">) => void): Unsubscribe;
	onDisableAllFreeAreas(cb: (p: MessagePayload<HostToClientMessage, "DisableAllFreeAreas">) => void): Unsubscribe;
	onEnableAllFreeAreas(cb: (p: MessagePayload<HostToClientMessage, "EnableAllFreeAreas">) => void): Unsubscribe;
	onUpdateFreeAreas(cb: (p: MessagePayload<HostToClientMessage, "UpdateFreeAreas">) => void): Unsubscribe;
	onFreeAreaEvent(cb: (p: MessagePayload<HostToClientMessage, "FreeAreaEvent">) => void): Unsubscribe;

	ackAllFreeAreasHidden(p: MessagePayload<ClientToHostMessage, "AllFreeAreasHidden">): void;
	ackAllFreeAreasShown(p: MessagePayload<ClientToHostMessage, "AllFreeAreasShown">): void;
	ackAllFreeAreasDisabled(p: MessagePayload<ClientToHostMessage, "AllFreeAreasDisabled">): void;
	ackAllFreeAreasEnabled(p: MessagePayload<ClientToHostMessage, "AllFreeAreasEnabled">): void;

	sendRewardAcquired(p: MessagePayload<ClientToHostMessage, "RewardAcquired">): void;
	sendAssetLoadingProgress(p: MessagePayload<ClientToHostMessage, "AssetLoadingProgress">): void;
	sendClientStartupLoadingStatus(p: MessagePayload<ClientToHostMessage, "ClientStartupLoadingStatus">): void;
	sendClientReady(p: MessagePayload<ClientToHostMessage, "ClientReady">): void;
	sendHeartBeat(p: MessagePayload<ClientToHostMessage, "HeartBeat">): void;
	sendFatalError(p: MessagePayload<ClientToHostMessage, "FatalError">): void;
	sendError(p: MessagePayload<ClientToHostMessage, "Error">): void;
	sendSetHitAreasInteractivity(p: MessagePayload<ClientToHostMessage, "SetHitAreasInteractivity">): void;
	sendUpdateFreeArea(p: MessagePayload<ClientToHostMessage, "UpdateFreeArea">): void;

	sendUiSnapshot(p: UiSnapshotPayload): void;
	sendUiSnapshotError(p: MessagePayload<ClientToHostMessage, "SnapshotError">): void;
}

const CommunicatorContext = createContext<CommunicatorContextValue | null>(null);

function cloneArrayBuffer(src: ArrayBuffer): ArrayBuffer {
	try {
		return src.slice(0);
	} catch {
		const copy = new Uint8Array(src.byteLength);
		copy.set(new Uint8Array(src));
		return copy.buffer;
	}
}

export function CommunicatorProvider({ children }: { children: ReactNode }) {
	const connector: ClientConnector = useMemo(() => new ClientConnector(), []);

	useEffect(() => {
		connector.ackInitConnection({ success: true });
	}, [connector]);

	const value = useMemo<CommunicatorContextValue>(
		() => ({
			connector,

			isStandalone: () => connector.isStandalone(),

			onHostError: (cb) => connector.onHostError(cb),
			onConfigConnection: (cb) => connector.onConfigConnection(cb),
			onStartupPreload: (cb) => connector.onStartupPreload(cb),
			onGameReady: (cb) => connector.onGameReady(cb),

			onBetUpdated: (cb) => connector.onBetUpdated(cb),
			onPromotionStarted: (cb) => connector.onPromotionStarted(cb),
			onPromotionEnded: (cb) => connector.onPromotionEnded(cb),
			onBuyFeatureStarted: (cb) => connector.onBuyFeatureStarted(cb),
			onBuyFeatureEnded: (cb) => connector.onBuyFeatureEnded(cb),
			onPaidRoundStarted: (cb) => connector.onPaidRoundStarted(cb),
			onPaidRoundEnded: (cb) => connector.onPaidRoundEnded(cb),
			onRoundStarted: (cb) => connector.onRoundStarted(cb),
			onRoundEnded: (cb) => connector.onRoundEnded(cb),
			onAnimationsStarted: (cb) => connector.onAnimationsStarted(cb),
			onAnimationsEnded: (cb) => connector.onAnimationsEnded(cb),

			onSnapshotMode: (cb) => connector.onSnapshotMode(cb),
			onSnapshotRequest: (cb) => connector.onSnapshotRequest(cb),

			ackInitConnection: (p) => connector.ackInitConnection(p),
			ackConfigConnection: (p) => connector.ackConfigConnection(p),

			demandFullscreen: () => connector.demandFullscreen(),
			exitFullscreen: () => connector.exitFullscreen(),
			onFullscreenGranted: (cb) => connector.onFullscreenGranted(cb),
			onExitFullscreenAck: (cb) => connector.onExitFullscreenAck(cb),
			onOrientationChange: (cb) => connector.onOrientationChange(cb),

			onHideAllFreeAreas: (cb) => connector.onHideAllFreeAreas(cb),
			onShowAllFreeAreas: (cb) => connector.onShowAllFreeAreas(cb),
			onDisableAllFreeAreas: (cb) => connector.onDisableAllFreeAreas(cb),
			onEnableAllFreeAreas: (cb) => connector.onEnableAllFreeAreas(cb),
			onUpdateFreeAreas: (cb) => connector.onUpdateFreeAreas(cb),
			onFreeAreaEvent: (cb) => connector.onFreeAreaEvent(cb),

			ackAllFreeAreasHidden: (p) => connector.ackAllFreeAreasHidden(p),
			ackAllFreeAreasShown: (p) => connector.ackAllFreeAreasShown(p),
			ackAllFreeAreasDisabled: (p) => connector.ackAllFreeAreasDisabled(p),
			ackAllFreeAreasEnabled: (p) => connector.ackAllFreeAreasEnabled(p),

			sendRewardAcquired: (p) => connector.sendRewardAquired(p),
			sendAssetLoadingProgress: (p) => connector.sendAssetLoadingProgress(p),
			sendClientStartupLoadingStatus: (p) => connector.sendClientStartupLoadingStatus(p),
			sendClientReady: (p) => connector.sendClientReady(p),
			sendHeartBeat: (p) => connector.sendHeartBeat(p),
			sendFatalError: (p) => connector.sendFatalError(p),
			sendError: (p) => connector.sendError(p),
			sendSetHitAreasInteractivity: (p) => connector.sendSetHitAreasInteractivity(p),
			sendUpdateFreeArea: (p) => connector.sendUpdateFreeArea(p),

			sendUiSnapshot: (p) => {
				if (!(p.bytes instanceof ArrayBuffer)) {
					debugWarn("[EF Snapshots] SnapshotUpdated dropped because bytes is not an ArrayBuffer", {
						kind: p.kind,
						sourceId: p.sourceId,
						areaIds: p.areaIds,
						revision: p.revision,
					});
					return;
				}

				const targetAreaIds = Array.isArray(p.areaIds)
					? p.areaIds.filter((areaId) => String(areaId).trim().length > 0)
					: [];

				if (targetAreaIds.length === 0) {
					debugWarn("[EF Snapshots] SnapshotUpdated dropped because no target areaIds were provided", {
						kind: p.kind,
						sourceId: p.sourceId,
						revision: p.revision,
					});
					return;
				}

				for (const areaId of targetAreaIds) {
					const bytesForTarget = cloneArrayBuffer(p.bytes);

					connector.sendSnapshotUpdated({
						surface: p.kind,
						surfaceId: String(areaId),
						rev: p.revision,
						mime: p.format,
						width: p.width,
						height: p.height,
						scale: p.scale,
						bytes: bytesForTarget,
					});
				}
			},

			sendUiSnapshotError: (p) => connector.sendSnapshotError(p),
		}),
		[connector]
	);

	return <CommunicatorContext.Provider value={value}>{children}</CommunicatorContext.Provider>;
}

export function useCommunicator(): CommunicatorContextValue {
	const ctx = useContext(CommunicatorContext);
	if (!ctx) {
		throw new Error("useCommunicator must be inside a CommunicatorProvider");
	}
	return ctx;
}
